import './style/main.scss'
import React, { useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import App from './component/app'
import device from 'current-device'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/es/integration/react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { persistStore } from 'redux-persist'
import RedirectToApp from './component/redirect-to-app'
import { store } from './lib/store'
import ReactGA from 'react-ga'
import { merchantTypes } from './lib/merchantUtils'
import isNil from 'lodash/isNil'
import { BrowserRouter } from 'react-router-dom'

const persistor = persistStore(store)



// Suppress cross-origin errors from deprecated gapi.auth2 before React's global handlers.
// "Script error." is the browser-sanitised form of any cross-origin script exception.
// gapi also rejects promises with plain objects (not Error), caught via unhandledrejection.
function isGapiNoise(msg) {
  if (!msg) return true
  const s = typeof msg === 'string' ? msg : (msg.message || String(msg))
  return (
    s === 'Script error.' ||
    s === 'Script error' ||
    s.includes('idpiframe_initialization_failed') ||
    s.includes('Not a valid origin') ||
    s.includes('fedcm_migration') ||
    s.includes('allow-scripts') && s.includes('allow-same-origin')
  )
}

window.addEventListener('error', (event) => {
  if (isGapiNoise(event.message)) {
    event.stopImmediatePropagation()
    event.preventDefault()
  }
}, true)

window.addEventListener('unhandledrejection', (event) => {
  if (isGapiNoise(event.reason?.message ?? event.reason)) {
    event.stopImmediatePropagation()
    event.preventDefault()
  }
}, true)

// MUST be at the very top of the file
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === "string") {
    if (args[0].includes("ReactDOM.render is no longer supported")) return;
    if (args[0].includes("redux-persist/createPersistoid")) return;
    if (args[0].includes("Converting circular structure to JSON")) return;
  }
  originalError(...args);
};

const originalWarn = console.warn;
console.warn = (...args) => {
  if (typeof args[0] === "string" && args[0].toLowerCase().includes("google")) {
    return; // suppress all Google warnings
  }
  originalWarn(...args);
};



// Defined once at module level — never recreated on re-render
const muiTheme = createTheme({
  overrides: {
    MuiSelect: {
      icon: { color: '#959595' }
    }
  },
  palette: {
    primary: { main: 'rgba(255, 255, 255)' },
    secondary: { main: 'rgba(88, 149, 0)' },
    background: { paper: '#1d1d1d' },
    action: {
      selected: 'rgba(0,0,0,0.8)',
      hover: 'rgba(0,0,0,0.4)'
    }
  }
})

// 'link' is the device pairing approval page (WEB-7089). It is reached by scanning a QR code
// shown on a car head unit, so it is nearly always opened on a phone - the one class of device
// this app otherwise bounces straight to the app store. Without the exclusion the pairing page
// can never be seen in the case it exists for.
// 'oauth/consent' (WEB-7106): the ChatGPT and Claude mobile apps open the consent page in a phone browser.
// 'connected-apps' (WEB-7107): the mobile apps and the AI-assistants help page link to it.
// 'artemis/credits' (WEB-7135): the mobile apps open Artemis's Buy more link in a phone browser.
const REDIRECT_EXCLUDED_PATHS = ['claim', 'EastmansProTrial', 'password-reset', 'link', 'oauth/consent', 'connected-apps', 'artemis/credits']
const PASSWORD_RESET_PATTERNS = [/\bpassword-reset\b/, /\btype=\b/, /\bredeem\b/]
const APP_STORE_URL = 'https://itunes.apple.com/us/app/basemap-own-the-outdoors/id1305237481?ls=1&mt=8'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.mobile.basemap'

function Main() {
  const [passwordReset, setPasswordReset] = useState(false)

  useEffect(() => {
    const pathname = window.location.pathname.substring(1)

    if (device.type === 'mobile' && !REDIRECT_EXCLUDED_PATHS.includes(pathname)) {
      window.location.href = /iPhone|iPad|Macintosh/.test(navigator.userAgent)
        ? APP_STORE_URL
        : PLAY_STORE_URL
      return
    }

    const url = window.location.href
    if (PASSWORD_RESET_PATTERNS.some(re => re.test(url))) {
      setPasswordReset(true)
    }

    ReactGA.initialize('UA-107140559-3', { debug: false, titleCase: false })
    ReactGA.pageview(window.location.pathname + window.location.search)
  }, [])

  const pathname = window.location.pathname.substring(1)
  const marketing = !isNil(merchantTypes[window.location.pathname.split('/')[1]])
  const showRedirect = device.type === 'mobile'
    && !passwordReset
    && !REDIRECT_EXCLUDED_PATHS.includes(pathname)
    && !marketing

  return (
    <Provider store={store}>
      {showRedirect ? (
        <RedirectToApp />
      ) : (
        <PersistGate persistor={persistor}>
          <ThemeProvider theme={muiTheme}>
            <BrowserRouter>
              {window.google && <App />}
            </BrowserRouter>
          </ThemeProvider>
        </PersistGate>
      )}
    </Provider>
  )
}

window.deferReactApp.then(() => {
  const container = document.getElementById('root')
  const root = createRoot(container)
  root.render(<Main />)
})
